import React from "react";

/**
 * ReportSelect — Reusable Dropdown Component
 * 
 * Features:
 * - Floating inset label matching DatePicker styling
 * - Custom chevron arrow icon on the right
 * - Consistent 38px height and Ocean Blue border/focus styling
 * 
 * Props:
 * - label: Floating label text
 * - value: Current selected value
 * - onChange: Change event handler
 * - options: Array of option objects or strings
 * - placeholder: Default placeholder option text
 * - valueKey: Key for option value (default: 'id' or 'value')
 * - labelKey: Key for option label (default: 'name' or 'label')
 * - disabled: Disabled state
 * - className: Custom container styles
 */
const ReportSelect = ({
  label,
  value,
  onChange,
  options = [],
  placeholder,
  valueKey,
  labelKey,
  disabled = false,
  className = "",
  ...props
}) => {
  return (
    <div className={["relative min-w-[170px] mt-1", className].filter(Boolean).join(" ")}>
      {label && (
        <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={[
            "form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium",
            disabled ? "bg-[#F8FAFC] text-ink-300 cursor-not-allowed" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((opt, idx) => {
            const val = typeof opt === "object" ? (valueKey ? opt[valueKey] : opt.id ?? opt.value) : opt;
            const text = typeof opt === "object" ? (labelKey ? opt[labelKey] : opt.name ?? opt.label ?? opt.branchName) : opt;
            return (
              <option key={val ?? idx} value={val}>
                {text}
              </option>
            );
          })}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default ReportSelect;
